/**
 * CarrierPositionLedger — the "defensible-vs-advocacy" reduction ledger.
 *
 * Renders brief.carrier_position (see backend war_room_carrier_position.py):
 * an engine-grounded carrier floor composed ONLY from certified engine outputs
 * (tier 1, attestable) plus the defendant seat's ASSERTED reductions (tier 3,
 * shown but NEVER applied to the floor).
 *
 * Design: K3 spec (scratch: k3_carrier_ledger_out.md). Signal logic —
 * orange (`--color-brand`) = the ONE primary figure identity (hero rail only);
 * green (`--color-verified`) = attestable/engine-grounded; red
 * (`--color-blocker`) = reserved for genuinely adverse; grayscale opacity
 * carries the rest (≈90% white = attestable fact, ≈55% = advocacy, ≈30% =
 * provenance/footnote). THE centerpiece decision: tier-3 advocacy is
 * quarantined BY STRUCTURE — its running-total column is broken (a chip, never
 * a number), so an asserted reduction is arithmetically impossible to misread
 * as part of the floor.
 *
 * Pure presentation — no fetching. Sections 5-7 (excluded footnote / caveats /
 * fail-safe) extend K3's truncated spec along its established pattern; flagged
 * for the pixel-review pass.
 */
import React from 'react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import type {
  CarrierPosition,
  CarrierLedgerLine as LedgerLine,
  ExposureLadder as ExposureLadderData,
  SubrogationAllocation as SubrogationAllocationData,
  DefendantPosition as DefendantPositionData,
} from '@/services/deliberationApi';
import type { MitigationFactorId } from '@/services/interpretationApi';
import { patchMitigationAttestation } from '@/services/interpretationApi';
import { creditMitigationFactor, clearMitigationCredit } from '@/services/mitigationCreditApi';

export type { CarrierPosition };

/**
 * Optional accountant actions on the exposure ladder (owner ruling 2026-09-21). Two
 * writers, two directions, NEVER one button:
 *   CREDIT  — insured's mitigation documented → engine floor UP (mitigationCreditApi).
 *   CONCEDE — carrier's failure-to-mitigate defense proven → mediator DOWN
 *             (interpretationApi PATCH p=1). Mutually exclusive with CREDIT per factor.
 * Rendered per rung ONLY when `canAttest` (accountant). A CREDIT/CONCEDE needs a staged
 * candidate cite; when none is staged the buttons are disabled (never fabricate a cite).
 */
export interface LadderAttest {
  reportId: string;
  canAttest: boolean;
  /** factor keys currently CONCEDED (interpretation p=1). */
  concededKeys: string[];
  /** staged candidate cite per factor (from interpretation/candidates). */
  citeByFactor: Record<string, { doc_id: string; quote: string }>;
  /** re-fetch the brief (+ interpretation) after an action. */
  onChanged: () => void | Promise<void>;
}

const MONO = 'font-mono tabular-nums';
const shortRun = (r?: string | null) => (r ? (r.length > 28 ? `${r.slice(0, 28)}…` : r) : '—');

/** Grayscale outlined chip — metadata, deliberately not red/orange. */
function MetaChip({ children }: { children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center ${MONO} uppercase`}
      style={{
        fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)', borderRadius: 2, padding: '1px 6px',
      }}
    >
      {children}
    </span>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ hero }: { hero: CarrierPosition['hero'] }) {
  const computed = hero.value != null;
  const mixedRuns = !!hero.run_id_note; // engines span runs → not attestable
  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        // border shorthand first, THEN the left longhands, so the brand rail wins
        // (the one orange element on screen — hero identity per K3 signal logic).
        border: '1px solid var(--color-border)',
        borderLeftStyle: 'solid',
        borderLeftWidth: 3,
        borderLeftColor: computed && !mixedRuns ? 'var(--color-brand)' : 'var(--color-border)',
        borderRadius: 2, padding: 18,
      }}
    >
      <div className={`${MONO} uppercase`} style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--color-text-muted)' }}>
        {hero.label}
      </div>
      {computed ? (
        <div className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
          <FinancialValue value={hero.value} cents className={`${MONO} text-[36px] leading-none font-medium`} />
          <span className="sr-only">carrier floor</span>
        </div>
      ) : (
        <div className="mt-1 cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 15 }}>
          Not computed — engine outputs unavailable
        </div>
      )}

      {/* fragility / provenance strip */}
      {(hero.default_mitigation || mixedRuns) && computed && (
        <div className="mt-3 pt-3 flex items-start gap-2" style={{ borderTop: '1px solid var(--color-border-faint, var(--color-border))' }}>
          <MetaChip>{mixedRuns ? 'Mixed runs' : 'Default factors'}</MetaChip>
          <p className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 12, margin: 0 }}>
            {mixedRuns
              ? hero.run_id_note
              : 'Mitigation credit computed on default factors — rebuttable; converges toward the supported net once actual factors are entered.'}
          </p>
        </div>
      )}
      {computed && (
        <div className={`${MONO} mt-2`} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
          run {shortRun(hero.run_id)} · {hero.basis}
        </div>
      )}
    </div>
  );
}

// ── Tier-1 attestable waterfall ──────────────────────────────────────────────
function Tier1Row({ line, isFinal }: { line: LedgerLine; isFinal: boolean }) {
  return (
    <div className="py-3" style={{ borderTop: '1px solid var(--color-border-faint, var(--color-border))' }}>
      <div className="flex items-baseline gap-3">
        <span className="mt-1.5 h-[5px] w-[5px] shrink-0" style={{ background: 'var(--color-verified)' }} title="engine-grounded" />
        <div className="flex-1 min-w-0">
          <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontSize: 13 }}>{line.label}</div>
          {line.engine_id && (
            <div className={MONO} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
              {line.engine_id} · run {shortRun(line.run_id)}
            </div>
          )}
        </div>
        <div className="text-right shrink-0" style={{ width: 120 }}>
          {line.delta != null
            ? <FinancialValue value={line.delta} contra cents className={MONO} />
            : <span className={MONO} style={{ color: 'var(--color-text-dim)', fontSize: 13 }}>—</span>}
          {line.pct != null && (
            <div className={MONO} style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{line.pct}% credit</div>
          )}
        </div>
        <div className="text-right shrink-0" style={{ width: 140 }}>
          <FinancialValue value={line.running} cents size={isFinal ? 'md' : 'sm'} className={`${MONO}`} />
          {isFinal && (
            <div style={{ borderTop: '1px solid var(--color-text-secondary)', marginTop: 3, paddingTop: 2, borderBottom: '1px solid var(--color-text-secondary)', height: 3 }} />
          )}
        </div>
      </div>
      {/* The dual-method variance is a data-quality caveat about the loss quantum,
          not part of the floor derivation — it reads in the footer (owner 2026-09-22),
          so the hero waterfall stays a clean "floor climbs" story. The $156,984.30
          reconciled-loss line itself stays; only its note moves down. */}
      {line.note && line.key !== 'reconciled_loss' && (
        <div className="mt-1.5 ml-5">
          {line.key === 'mitigation' && line.note.includes('DEFAULT') && (
            <div className="mb-1"><MetaChip>Default factors</MetaChip></div>
          )}
          <p className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 12, margin: 0 }}>
            <span className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', marginRight: 6 }}>Note —</span>
            {line.note}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Tier-3 advocacy quarantine (running column BROKEN — chip, never a number) ─
function Tier3Row({ line }: { line: LedgerLine }) {
  return (
    <div className="py-3" style={{ borderTop: '1px solid var(--color-border-faint, var(--color-border))' }}>
      <div className="flex items-baseline gap-3">
        <span className="mt-1.5 h-[5px] w-[5px] shrink-0" style={{ border: '1px solid var(--color-text-muted)' }} title="asserted, not applied" />
        <div className="flex-1 min-w-0">
          <div className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{line.label}</div>
          {line.source && (
            <div className={MONO} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>{line.source}</div>
          )}
        </div>
        <div className="text-right shrink-0" style={{ width: 120 }}>
          {line.would_reduce_by != null && (
            <>
              {/* label + nowrap figure (K3 nit #1 — avoid the mid-phrase wrap) */}
              <div className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.06em', color: 'var(--color-text-dim)' }}>would reduce</div>
              <div className={MONO} style={{ fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                <FinancialValue value={line.would_reduce_by} compact cents className={MONO} />
              </div>
            </>
          )}
          {line.asserted_value != null && line.asserted_value <= 1 && (
            <div className={MONO} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>{(line.asserted_value * 100).toFixed(1)}% asserted</div>
          )}
        </div>
        <div className="text-right shrink-0" style={{ width: 140 }}>
          <MetaChip>Not applied</MetaChip>
        </div>
      </div>
      {line.argument && (
        <p className="mt-1.5 ml-5 cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 12, margin: '6px 0 0 20px' }}>
          <span className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', marginRight: 6 }}>Carrier argues —</span>
          {line.argument}
        </p>
      )}
    </div>
  );
}

// ── Exposure ladder (documentation-contingent; PROJECTION, read-only) ─────────
// The carrier floor climbs as each mitigation factor's evidence is credited: each
// open rung's `running_floor_if_credited` is where the floor lands IF that factor
// (and those above it) are credited — a projection, never an attested amount. The
// current floor is the hero (orange); rungs are muted projections; a credited factor
// is green. Fully documented → net-claim ceiling.
// Small ledger action button — grayscale, restrained (no orange; the hero owns that).
function LadderBtn({ onClick, disabled, busy, children, title }: {
  onClick: () => void; disabled?: boolean; busy?: boolean; children: React.ReactNode; title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      title={title}
      className={`${MONO} uppercase`}
      style={{
        fontSize: 9, letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 2,
        border: '1px solid var(--color-border)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-secondary)',
        background: 'transparent', cursor: disabled || busy ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {busy ? '…' : children}
    </button>
  );
}

function ExposureLadder({ ladder, attest }: { ladder?: ExposureLadderData | null; attest?: LadderAttest }) {
  const [busy, setBusy] = React.useState<string | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  // CONCEDE requires a REAL page cite (confirm-on-open — same discipline as the
  // Attestation Board). The staged candidate carries page=null, so we NEVER fabricate
  // one: the accountant confirms the page inline before the concede commits. CREDIT
  // needs no page (its engine cite is a free string), so it stays one-click.
  const [concedeKey, setConcedeKey] = React.useState<string | null>(null);
  const [pageInput, setPageInput] = React.useState<string>('');
  if (!ladder || !ladder.available || !ladder.rungs || ladder.rungs.length === 0) return null;
  const rungs = ladder.rungs;
  const openCount = ladder.open_factor_count ?? rungs.filter((r) => !r.credited).length;
  const showActions = !!attest?.canAttest;
  const conceded = new Set(attest?.concededKeys || []);

  const run = async (tag: string, fn: () => Promise<unknown>): Promise<boolean> => {
    if (!attest) return false;
    setBusy(tag); setErr(null);
    try { await fn(); await attest.onChanged(); return true; }
    catch (e) { setErr(e instanceof Error ? e.message : 'Action failed'); return false; }
    finally { setBusy(null); }
  };

  const citeFor = (key: string) => attest?.citeByFactor?.[key] || null;

  const openConcede = (rKey: string) => { setConcedeKey(rKey); setPageInput(''); setErr(null); };
  const cancelConcede = () => { setConcedeKey(null); setPageInput(''); };

  const commitConcede = async (rKey: string, cite: { doc_id: string; quote: string }) => {
    const page = parseInt(pageInput, 10);
    if (!Number.isInteger(page) || page < 1 || String(page) !== pageInput.trim()) {
      setErr('Concede needs the page you confirmed on the document (≥ 1).');
      return;
    }
    const fid = rKey as MitigationFactorId;
    const ok = await run(`concede:${rKey}`, () =>
      patchMitigationAttestation(attest!.reportId, {
        id: fid, p: 1,
        evidence_cite: [{ doc_id: cite.doc_id, page, quote: cite.quote }],
        attestor_role: 'accountant',
      }),
    );
    if (ok) { setConcedeKey(null); setPageInput(''); }
  };

  // A plain render helper (NOT a nested component) so the concede page <input> keeps
  // focus across keystrokes — a nested component gets a fresh identity every render and
  // React would remount (and blur) the field on each character.
  const renderRungActions = (rKey: string, credited: boolean): React.ReactNode => {
    if (!showActions) return null;
    const isConceded = conceded.has(rKey);
    const cite = citeFor(rKey);
    const fid = rKey as MitigationFactorId;
    if (credited) {
      return (
        <LadderBtn busy={busy === `clear:${rKey}`} title="Reverse this credit — the deduction re-applies and the floor drops back"
          onClick={() => run(`clear:${rKey}`, () => clearMitigationCredit(attest!.reportId, fid))}>
          Clear
        </LadderBtn>
      );
    }
    if (isConceded) {
      return (
        <div className="flex items-center gap-1.5">
          <MetaChip>Conceded</MetaChip>
          <LadderBtn busy={busy === `unconcede:${rKey}`} title="Undo the concession (mediator neutral returns toward N)"
            onClick={() => run(`unconcede:${rKey}`, () => patchMitigationAttestation(attest!.reportId, { id: fid, p: 0, evidence_cite: [] }))}>
            Undo
          </LadderBtn>
        </div>
      );
    }
    const composerOpen = concedeKey === rKey;
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <LadderBtn busy={busy === `credit:${rKey}`} disabled={!cite}
            title={cite ? 'Insured DID mitigate (documented) → carrier floor UP' : 'No evidence staged — cannot credit'}
            onClick={() => run(`credit:${rKey}`, () => creditMitigationFactor(attest!.reportId, fid, `${cite!.quote} [doc ${cite!.doc_id}]`, cite!.doc_id))}>
            Credit
          </LadderBtn>
          <LadderBtn busy={busy === `concede:${rKey}`} disabled={!cite || composerOpen}
            title={cite ? "Carrier's failure-to-mitigate defense PROVEN → mediator neutral E[X] DOWN (a different figure than this floor)" : 'No evidence staged — cannot concede'}
            onClick={() => openConcede(rKey)}>
            Concede
          </LadderBtn>
        </div>
        {composerOpen && cite && (
          <div className="flex items-center gap-2 flex-wrap" style={{ borderLeft: '2px solid var(--color-border)', paddingLeft: 8 }}>
            <span className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)' }}>
              doc {cite.doc_id} · confirm page
            </span>
            <input
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="pg"
              inputMode="numeric"
              aria-label={`page for ${rKey} concession`}
              className={MONO}
              style={{
                width: 46, fontSize: 11, padding: '2px 6px', borderRadius: 2, textAlign: 'right',
                border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-primary)',
              }}
            />
            <LadderBtn busy={busy === `concede:${rKey}`} disabled={!pageInput}
              title="Record the concession — mediator E[X] drops by this factor's engine Cᵢ"
              onClick={() => commitConcede(rKey, cite)}>
              Confirm concede
            </LadderBtn>
            <LadderBtn onClick={cancelConcede}>Cancel</LadderBtn>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <TierLegend text="Exposure ladder — floor climbs as mitigation is documented" color="var(--color-text-muted)" />
      <div style={{ borderLeft: '1px solid var(--color-border)', paddingLeft: 12, marginLeft: 2 }}>
        {rungs.map((r) => (
          <div key={r.key} className="py-2.5" style={{ borderTop: '1px solid var(--color-border-faint, var(--color-border))' }}>
            <div className="flex items-baseline gap-3">
              <span
                className="mt-1.5 h-[5px] w-[5px] shrink-0"
                style={r.credited
                  ? { background: 'var(--color-verified)' }
                  : { border: '1px solid var(--color-text-muted)' }}
                title={r.credited ? 'credited' : 'projected — pending attestation'}
              />
              <div className="flex-1 min-w-0">
                <div className="cs-body" style={{ color: r.credited ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', fontSize: 13 }}>
                  {r.label}
                </div>
                {r.evidence_cite ? (
                  <p className="cs-body" style={{ color: 'var(--color-text-dim)', fontSize: 11, margin: '2px 0 0' }}>
                    <span className={`${MONO} uppercase`} style={{ fontSize: 9, marginRight: 6 }}>Evidence —</span>
                    “{r.evidence_cite.length > 90 ? `${r.evidence_cite.slice(0, 90)}…` : r.evidence_cite}”
                  </p>
                ) : (
                  !r.credited && <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', marginTop: 2 }}>no evidence staged yet</div>
                )}
                {showActions && <div className="mt-2">{renderRungActions(r.key, r.credited)}</div>}
              </div>
              <div className="text-right shrink-0" style={{ width: 120 }}>
                {r.credited ? (
                  <MetaChip>Credited</MetaChip>
                ) : (
                  <>
                    <div className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.06em', color: 'var(--color-text-dim)' }}>if credited</div>
                    <div className={MONO} style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                      +<FinancialValue value={r.deduction} compact cents className={MONO} />
                    </div>
                  </>
                )}
              </div>
              <div className="text-right shrink-0" style={{ width: 140 }}>
                <FinancialValue value={r.running_floor_if_credited} cents size="sm" className={MONO} />
                <div className={`${MONO} uppercase`} style={{ fontSize: 8, color: 'var(--color-text-dim)', letterSpacing: '0.06em' }}>
                  {r.credited ? 'floor' : 'projected floor'}
                </div>
              </div>
            </div>
          </div>
        ))}
        {err && (
          <p className="cs-body" style={{ color: 'var(--color-blocker)', fontSize: 11, margin: '8px 0 0' }}>{err}</p>
        )}
        {/* Ceiling — fully documented → net claim (Gold N; never moved by a projection). */}
        {ladder.ceiling != null && (
          <div className="py-2.5 flex items-baseline gap-3" style={{ borderTop: '1px solid var(--color-text-secondary)' }}>
            <span className="mt-1.5 h-[5px] w-[5px] shrink-0" style={{ background: 'var(--color-text-secondary)' }} />
            <div className="flex-1 min-w-0 cs-body" style={{ color: 'var(--color-text-primary)', fontSize: 13 }}>
              Fully documented — net claim ceiling
            </div>
            <div className="text-right shrink-0" style={{ width: 140 }}>
              <FinancialValue value={ladder.ceiling} cents className={`${MONO} font-medium`} />
            </div>
          </div>
        )}
      </div>
      <p className="cs-body" style={{ color: 'var(--color-text-dim)', fontSize: 11, margin: '8px 0 0' }}>
        {openCount > 0
          ? `${openCount} factor${openCount === 1 ? '' : 's'} open. Each projected floor is what the carrier floor becomes once that factor's evidence is credited (accountant attestation) — a projection, not a proven amount.`
          : 'All mitigation factors credited — the floor is at the net-claim ceiling.'}
      </p>
    </>
  );
}

function TierLegend({ text, color }: { text: string; color: string }) {
  return (
    <div className="flex items-center gap-2 mt-4 mb-1">
      <span className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.08em', color }}>{text}</span>
      <span className="flex-1" style={{ height: 1, background: 'var(--color-border)' }} />
    </div>
  );
}

/**
 * Phase 5 §4 subrogation allocation. Shown only when the brief carries the block (confirmed
 * third-party). Side-aware: the plaintiff reads the "Anticipated carrier subrogation lien"
 * (strategy / work product) that determines the client's NET; a carrier reads its own allocation.
 * Three buckets (carrier recovers / insured keeps / fees) that foot to the gross recovery.
 */
function SubrogationAllocation({ block }: { block?: SubrogationAllocationData | null }) {
  if (!block) return null;
  const strategy = block.sensitivity === 'strategy';
  const buckets: Array<{ label: string; value: string | null; strong?: boolean }> = [
    { label: strategy ? 'Carrier lien' : 'Carrier recovers', value: block.carrier_recovers ?? null },
    { label: 'Insured keeps', value: block.insured_keeps ?? null, strong: true },
    { label: 'Attorney fees + costs', value: block.fees_applied ?? null },
  ];
  const hasBuckets = buckets.some((b) => b.value != null);
  return (
    <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--color-border)' }} data-testid="subrogation-allocation">
      <TierLegend
        text={strategy ? 'Subrogation — strategy, excluded from exports' : 'Subrogation allocation'}
        color="var(--color-text-secondary)"
      />
      <div className="flex items-baseline justify-between gap-3 mt-1 mb-2">
        <h3 className="cs-display" style={{ fontSize: 15, lineHeight: 1.15 }}>{block.label}</h3>
        <div className="flex items-center gap-1.5">
          {block.doctrine && <MetaChip>{block.doctrine.replace(/_/g, '-')}</MetaChip>}
          {block.fee_apportioned && <MetaChip>common-fund apportioned</MetaChip>}
          {block.pro_forma && <MetaChip>pro forma</MetaChip>}
          {block.attestable
            ? <MetaChip>attestable</MetaChip>
            : <MetaChip>figures pending</MetaChip>}
        </div>
      </div>

      {hasBuckets ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 4, columnGap: 16 }}>
          {buckets.map((b) => (
            <React.Fragment key={b.label}>
              <span className="cs-body" style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{b.label}</span>
              <FinancialValue
                value={b.value}
                cents
                className={`${MONO} text-right${b.strong ? ' font-medium' : ''}`}
              />
            </React.Fragment>
          ))}
          <span className="cs-body" style={{ fontSize: 12, color: 'var(--color-text-dim)', paddingTop: 4, borderTop: '1px solid var(--color-border)' }}>
            Gross recovery{block.pro_forma ? ' (if the policy-limits demand were paid in full)' : ''}
          </span>
          <span className={`${MONO} text-right`} style={{ fontSize: 12, color: 'var(--color-text-dim)', paddingTop: 4, borderTop: '1px solid var(--color-border)' }}>
            <FinancialValue value={block.gross_recovery ?? null} cents compact className={MONO} />
          </span>
        </div>
      ) : (
        <p className="cs-body" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
          No allocation computed yet — the attorney has not attested the recovery and payment figures.
        </p>
      )}

      {block.note && (
        <p className="cs-body mt-2" style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>{block.note}</p>
      )}
      {block.caveats.length > 0 && (
        <ul className="mt-2 space-y-1" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {block.caveats.map((c, i) => (
            <li key={i} className="cs-body flex gap-2" style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}>
              <span style={{ color: 'var(--color-text-dim)' }}>—</span>{c.replace(/_/g, ' ')}
            </li>
          ))}
        </ul>
      )}
      {block.doctrine_disclaimers && block.doctrine_disclaimers.length > 0 && (
        <div className="mt-3 pt-2" style={{ borderTop: '1px solid var(--color-border)' }}>
          <TierLegend text="Doctrine & jurisdiction — attorney confirms" color="var(--color-text-dim)" />
          <ul className="mt-1 space-y-1.5" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {block.doctrine_disclaimers.map((c, i) => (
              <li key={i} className="cs-body flex gap-2" style={{ color: 'var(--color-text-dim)', fontSize: 11, lineHeight: 1.45 }}>
                <span aria-hidden>—</span><span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Phase 6 M3 — defendant-position read-only block (strategy, excluded from exports).
 * Mirrors the SubrogationAllocation sub-component pattern.
 */
function DefendantPosition({ block }: { block?: DefendantPositionData | null }) {
  if (!block) return null;
  const strategy = block.sensitivity === 'strategy';

  const renderSideBySide = (row?: DefendantPositionData['defendant_after_fault']) => {
    if (!row) return null;
    const zeroDelta = row.delta == null || Number(row.delta) === 0;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <div className="text-right">
          <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', letterSpacing: '0.06em' }}>Plaintiff</div>
          <div className={`${MONO} text-right`} style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
            {row.plaintiff != null ? <FinancialValue value={row.plaintiff} cents compact className={MONO} /> : '—'}
          </div>
        </div>
        <div className="text-right">
          <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', letterSpacing: '0.06em' }}>Defendant</div>
          <div className={`${MONO} text-right`} style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
            {row.defendant != null ? <FinancialValue value={row.defendant} cents compact className={MONO} /> : '—'}
          </div>
        </div>
        <div className="text-right">
          <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', letterSpacing: '0.06em' }}>Δ</div>
          <div className={`${MONO} text-right`} style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            {zeroDelta ? '—' : <FinancialValue value={row.delta} cents compact contra className={MONO} />}
          </div>
        </div>
      </div>
    );
  };

  const sideBySideKeys: Array<{ key: keyof DefendantPositionData; label: string }> = [
    { key: 'defendant_after_fault', label: 'After comparative fault' },
    { key: 'uncapped', label: 'Uncapped' },
    { key: 'policy_limits_figure', label: 'Policy limits' },
  ];

  return (
    <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--color-border)' }} data-testid="defendant-position">
      <TierLegend
        text={strategy ? 'Defendant position — strategy, excluded from exports' : 'Defendant position'}
        color="var(--color-text-secondary)"
      />
      <div className="flex items-baseline justify-between gap-3 mt-1 mb-2">
        <h3 className="cs-display" style={{ fontSize: 15, lineHeight: 1.15 }}>Defendant position</h3>
        <div className="flex items-center gap-1.5">
          <MetaChip>lowest engine-supported</MetaChip>
          <MetaChip>strategy cross-check</MetaChip>
        </div>
      </div>

      {block.defendant_quantum && (
        <div className="mb-3">
          <div className={`${MONO} text-right`} style={{ fontSize: 18, color: 'var(--color-text-primary)' }}>
            <FinancialValue value={block.defendant_quantum} cents compact className={MONO} />
          </div>
          {block.qualified_range && (
            <div className={`${MONO} text-right`} style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              <FinancialValue value={block.qualified_range.min} cents compact className={MONO} />
              {' – '}
              <FinancialValue value={block.qualified_range.max} cents compact className={MONO} />
            </div>
          )}
        </div>
      )}

      {sideBySideKeys.map((s) => {
        const row = block[s.key] as DefendantPositionData['defendant_after_fault'] | undefined;
        if (!row) return null;
        // Collapse the duplicate "Uncapped" row when no prejudgment interest was applied
        // (it equals the after-fault figure) — showing it twice reads as a bug.
        if (s.key === 'uncapped') {
          const af = block.defendant_after_fault;
          if (af && row.plaintiff === af.plaintiff && row.defendant === af.defendant) return null;
        }
        return (
          <div key={s.key} className="mb-3">
            <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', letterSpacing: '0.06em', marginBottom: 4 }}>
              {s.label}
            </div>
            {renderSideBySide(row)}
          </div>
        );
      })}

      {block.excluded_methods && block.excluded_methods.length > 0 && (
        <div className="mt-2 mb-2">
          <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', letterSpacing: '0.06em', marginBottom: 4 }}>
            methods not relied on
          </div>
          {block.excluded_methods.map((m, i) => (
            <div key={i} className="cs-body flex items-baseline justify-between gap-2" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              <span>
                <span style={{ textTransform: 'capitalize' }}>{(m.name || '').replace(/_/g, ' ')}</span>
                <span className={MONO} style={{ fontSize: 10, color: 'var(--color-text-dim)', marginLeft: 6 }}>{(m.status || '').replace(/_/g, ' ')}</span>
              </span>
              {m.net != null && <FinancialValue value={m.net} cents compact className={`${MONO} text-right`} />}
            </div>
          ))}
        </div>
      )}

      {(block.acv_total != null || block.rcv_total != null) && (
        <div className="mt-2 mb-2 cs-body" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
          Property: ACV {block.acv_total != null ? <FinancialValue value={block.acv_total} cents compact className={MONO} /> : <span className={MONO}>—</span>} · RCV {block.rcv_total != null ? <FinancialValue value={block.rcv_total} cents compact className={MONO} /> : <span className={MONO}>—</span>}
          {block.extra_expense_in_demand === false && (
            <span className={MONO} style={{ color: 'var(--color-text-dim)', fontSize: 11, marginLeft: 8 }}>no extra-expense reduction</span>
          )}
        </div>
      )}

      <div className="mt-2 mb-2 cs-body" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
        Collateral benefit received: {block.collateral_benefits_received != null ? <FinancialValue value={block.collateral_benefits_received} cents compact className={MONO} /> : <span className={MONO}>—</span>}
        {block.collateral_reduced === false && (
          <span className={MONO} style={{ color: 'var(--color-text-dim)', fontSize: 11, marginLeft: 8 }}>no reduction applied</span>
        )}
      </div>

      {block.defenses && block.defenses.length > 0 && (
        <div className="mt-3 mb-2">
          <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', letterSpacing: '0.06em', marginBottom: 4 }}>
            Defenses
          </div>
          {block.defenses.map((d) => (
            <div key={d.key} className="mb-2">
              <div className="cs-body" style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{d.label}</div>
              {d.argument ? (
                <p className="cs-body" style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: '2px 0 0' }}>
                  {d.argument}
                </p>
              ) : (
                <p className="cs-body" style={{ fontSize: 11, color: 'var(--color-text-dim)', margin: '2px 0 0' }}>
                  No seat argument.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {block.caveats && block.caveats.length > 0 && (
        <ul className="mt-2 space-y-1" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {block.caveats.map((c, i) => (
            <li key={i} className="cs-body flex gap-2" style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}>
              <span style={{ color: 'var(--color-text-dim)' }}>—</span>{c.replace(/_/g, ' ')}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Ledger ───────────────────────────────────────────────────────────────────
export function CarrierPositionLedger({ data, attest }: { data?: CarrierPosition | null; attest?: LadderAttest }) {
  if (!data) return null;
  const tier1 = data.ledger.filter((l) => l.tier === 1);
  const tier3 = data.ledger.filter((l) => l.tier === 3);
  const excluded = data.excluded_llm_figures || null;
  // K3 nit #2 — the backend caveats aggregate the line notes; drop any caveat that
  // already appears verbatim as a RENDERED line note, so it reads once, not twice.
  // reconciled_loss is excluded: its note (the dual-method variance) is deliberately
  // not rendered on the line, so it must survive here to show in the footer instead.
  const lineNotes = new Set(
    data.ledger.filter((l) => l.key !== 'reconciled_loss').map((l) => l.note).filter(Boolean) as string[],
  );
  const caveats = (data.caveats || []).filter((c) => !lineNotes.has(c));

  return (
    <div
      style={{ maxWidth: 720, background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 2, padding: 24 }}
      data-testid="carrier-position-ledger"
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="cs-display" style={{ fontSize: 20, lineHeight: 1.1 }}>Carrier Position — Defensible Floor</h2>
        {data.hero.attestable && (
          <span className="inline-flex items-center gap-1.5" style={{ fontSize: 9 }}>
            <span className="h-[6px] w-[6px]" style={{ background: 'var(--color-verified)' }} />
            <span className={`${MONO} uppercase`} style={{ letterSpacing: '0.08em', color: 'var(--color-text-secondary)' }}>Attestable</span>
          </span>
        )}
      </div>

      <Hero hero={data.hero} />

      {tier1.length > 0 && (
        <>
          <TierLegend text="Engine-grounded · attestable" color="var(--color-verified)" />
          {tier1.map((l, i) => <Tier1Row key={l.key} line={l} isFinal={i === tier1.length - 1} />)}
        </>
      )}

      <ExposureLadder ladder={data.exposure_ladder} attest={attest} />

      <SubrogationAllocation block={data.subrogation_allocation} />

      <DefendantPosition block={data.defendant_position} />

      {tier3.length > 0 && (
        <>
          <TierLegend text="Carrier advocacy — asserted, not applied" color="var(--color-text-muted)" />
          <div style={{ borderLeft: '1px solid var(--color-border)', paddingLeft: 12, marginLeft: 2 }}>
            {tier3.map((l) => <Tier3Row key={l.key} line={l} />)}
          </div>
        </>
      )}

      {excluded && (excluded.alternative_damages != null || excluded.defense_mediation_position != null) && (
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div className={`${MONO}`} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
            Defendant LLM synthesis — cross-checks only, never composed:
            {excluded.alternative_damages != null && <> alternative damages <FinancialValue value={excluded.alternative_damages as number} compact className={MONO} /></>}
            {excluded.defense_mediation_position != null && <> · mediation position <FinancialValue value={excluded.defense_mediation_position as number} compact className={MONO} /></>}
          </div>
        </div>
      )}

      {caveats.length > 0 && (
        <ul className="mt-3 space-y-1" style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
          {caveats.map((c, i) => (
            <li key={i} className="cs-body flex gap-2" style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>
              <span style={{ color: 'var(--color-text-dim)' }}>—</span>{c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CarrierPositionLedger;
